import { useState, useCallback } from 'react';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';

export type DateRangePreset =
  | 'all_time'
  | 'last_12_months'
  | 'last_18_months'
  | 'last_24_months'
  | 'last_3_years'
  | 'current_year'
  | 'previous_year'
  | '2024'
  | '2023'
  | 'custom';

export interface DateRange {
  from: Date | null;
  to: Date | null;
}

export interface DateRangeValue {
  preset: DateRangePreset;
  range: DateRange;
}

const PRESET_LABELS: Record<DateRangePreset, string> = {
  all_time: 'All Time',
  last_12_months: 'Last 12 months',
  last_18_months: 'Last 18 months',
  last_24_months: 'Last 24 months',
  last_3_years: 'Last 3 years',
  current_year: '2026',
  previous_year: '2025',
  '2024': '2024',
  '2023': '2023',
  custom: 'Custom range',
};

function getPresetRange(preset: DateRangePreset): DateRange {
  const now = new Date();
  switch (preset) {
    case 'all_time':
      return { from: null, to: null };
    case 'last_12_months': {
      const from = new Date(now);
      from.setMonth(from.getMonth() - 12);
      return { from, to: now };
    }
    case 'last_18_months': {
      const from = new Date(now);
      from.setMonth(from.getMonth() - 18);
      return { from, to: now };
    }
    case 'last_24_months': {
      const from = new Date(now);
      from.setMonth(from.getMonth() - 24);
      return { from, to: now };
    }
    case 'last_3_years': {
      const from = new Date(now);
      from.setFullYear(from.getFullYear() - 3);
      return { from, to: now };
    }
    case 'current_year':
      return { from: new Date(2026, 0, 1), to: new Date(2026, 11, 31) };
    case 'previous_year':
      return { from: new Date(2025, 0, 1), to: new Date(2025, 11, 31) };
    case '2024':
      return { from: new Date(2024, 0, 1), to: new Date(2024, 11, 31) };
    case '2023':
      return { from: new Date(2023, 0, 1), to: new Date(2023, 11, 31) };
    case 'custom':
      return { from: null, to: null };
  }
}

const PRESETS_GROUP_1: DateRangePreset[] = [
  'all_time',
  'last_12_months',
  'last_18_months',
  'last_24_months',
  'last_3_years',
];

const PRESETS_GROUP_2: DateRangePreset[] = [
  'current_year',
  'previous_year',
  '2024',
  '2023',
];

interface DateRangeFilterProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
}

export function DateRangeFilter({ value, onChange }: DateRangeFilterProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRangeValue>(value);

  const handleOpen = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        setDraft(value);
      }
      setOpen(isOpen);
    },
    [value]
  );

  const selectPreset = (preset: DateRangePreset) => {
    if (preset === 'custom') {
      setDraft((prev) => ({ preset: 'custom', range: prev.range.from ? prev.range : { from: null, to: null } }));
    } else {
      setDraft({ preset, range: getPresetRange(preset) });
    }
  };

  const applyFilter = () => {
    onChange(draft);
    setOpen(false);
  };

  const cancel = () => {
    setDraft(value);
    setOpen(false);
  };

  const displayLabel =
    value.preset === 'custom' && value.range.from
      ? `${format(value.range.from, 'MMM d, yyyy')} – ${value.range.to ? format(value.range.to, 'MMM d, yyyy') : '...'}`
      : PRESET_LABELS[value.preset];

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full sm:w-[220px] justify-start text-left font-normal bg-card border-border rounded-[8px] h-10"
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-50 bg-card border-border rounded-[8px]" align="start">
        <div className="p-3 space-y-1 min-w-[240px]">
          <p className="text-xs font-medium text-muted-foreground mb-2 px-1">Date Range</p>

          {PRESETS_GROUP_1.map((preset) => (
            <button
              key={preset}
              onClick={() => selectPreset(preset)}
              className={cn(
                'flex items-center w-full rounded-md px-2 py-1.5 text-sm cursor-pointer transition-colors',
                draft.preset === preset
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'hover:bg-muted text-foreground'
              )}
            >
              <span
                className={cn(
                  'mr-2 h-3.5 w-3.5 rounded-full border-2 flex items-center justify-center',
                  draft.preset === preset ? 'border-primary' : 'border-muted-foreground/40'
                )}
              >
                {draft.preset === preset && (
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                )}
              </span>
              {PRESET_LABELS[preset]}
            </button>
          ))}

          <Separator className="my-2" />

          {PRESETS_GROUP_2.map((preset) => (
            <button
              key={preset}
              onClick={() => selectPreset(preset)}
              className={cn(
                'flex items-center w-full rounded-md px-2 py-1.5 text-sm cursor-pointer transition-colors',
                draft.preset === preset
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'hover:bg-muted text-foreground'
              )}
            >
              <span
                className={cn(
                  'mr-2 h-3.5 w-3.5 rounded-full border-2 flex items-center justify-center',
                  draft.preset === preset ? 'border-primary' : 'border-muted-foreground/40'
                )}
              >
                {draft.preset === preset && (
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                )}
              </span>
              {PRESET_LABELS[preset]}
            </button>
          ))}

          <Separator className="my-2" />

          <button
            onClick={() => selectPreset('custom')}
            className={cn(
              'flex items-center w-full rounded-md px-2 py-1.5 text-sm cursor-pointer transition-colors',
              draft.preset === 'custom'
                ? 'bg-primary/10 text-primary font-medium'
                : 'hover:bg-muted text-foreground'
            )}
          >
            <span
              className={cn(
                'mr-2 h-3.5 w-3.5 rounded-full border-2 flex items-center justify-center',
                draft.preset === 'custom' ? 'border-primary' : 'border-muted-foreground/40'
              )}
            >
              {draft.preset === 'custom' && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              )}
            </span>
            Custom range
          </button>

          {draft.preset === 'custom' && (
            <div className="space-y-3 pt-2 px-1">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">From</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal rounded-[8px] h-9 text-sm',
                        !draft.range.from && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                      {draft.range.from ? format(draft.range.from, 'PPP') : 'Start date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[60] bg-card" align="start">
                    <Calendar
                      mode="single"
                      selected={draft.range.from ?? undefined}
                      onSelect={(date) =>
                        setDraft((prev) => ({
                          ...prev,
                          range: { ...prev.range, from: date ?? null },
                        }))
                      }
                      initialFocus
                      className={cn('p-3 pointer-events-auto')}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">To</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal rounded-[8px] h-9 text-sm',
                        !draft.range.to && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                      {draft.range.to ? format(draft.range.to, 'PPP') : 'End date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[60] bg-card" align="start">
                    <Calendar
                      mode="single"
                      selected={draft.range.to ?? undefined}
                      onSelect={(date) =>
                        setDraft((prev) => ({
                          ...prev,
                          range: { ...prev.range, to: date ?? null },
                        }))
                      }
                      disabled={(date) =>
                        draft.range.from ? date < draft.range.from : false
                      }
                      initialFocus
                      className={cn('p-3 pointer-events-auto')}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          )}

          <Separator className="my-2" />

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={cancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={applyFilter}
              disabled={draft.preset === 'custom' && !draft.range.from}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
